import React, { useEffect, useRef, useState } from "react";
import { arrow, autoUpdate, flip, offset, shift, size, useClick, useFloating, useFocus, useHover, useInteractions, useRole, } from "@floating-ui/react";
import { useTheme } from "styled-components";
import { TooltipBubble } from "./TooltipBubble";
/**
 * Default delay for hiding the Tooltip.
 */
const TOOLTIP_HIDE_DELAY = 3000;
const ARROW_HEIGHT = 7;
const GAP = 2;
const OFFSET_AMOUNT = ARROW_HEIGHT + GAP;
/**
 * A Tooltip is an additional description that is displayed when a user hovers
 * over, clicks or gives focus to a UI element.
 *
 * @example
 * <Tooltip content="Tooltip text.">
 *   <Button size="medium">Hover me</Button>
 * </Tooltip>
 */
export const Tooltip = ({ children, content, autoHide = false, placement = "bottom", open = false, trigger = "hover", testId, variant = "dark", }) => {
    if (!children)
        throw new Error("Tooltip is missing a child");
    const theme = useTheme();
    const [visible, setVisible] = useState(open);
    const arrowRef = useRef(null);
    const { context, floatingStyles, refs } = useFloating({
        // shift: prevents the floating element from overflowing a clipping container by shifting it to stay in view.
        // flip: prevents the floating element from overflowing a clipping container by flipping it to the opposite placement to stay in view.
        // size: resizes the floating element, for example so it will not overflow a clipping container, or to match the width of the reference element.
        // arrow: provides data to position an inner element of the floating element such that it is centered to its reference element.
        // ----
        // offset() should generally be placed at the beginning of your middleware array.
        // Placing shift() before flip() in the array ensures it can do its work before flip() tries to change the placement.
        // arrow() has to go after shift() so the transformations it does to the x and y coords means it will overflow the edge despite shift being in use.
        middleware: [
            offset(OFFSET_AMOUNT), // offset is needed because of the arrow
            shift(),
            flip(),
            arrow({ element: arrowRef }),
            // I am modifying size below, because after a shift and a flip, floating-ui is not calculating the width properly,
            // which adds overflow to the tooltip's parent container. This trick seems to fix the issue.
            // Perhaps in a future version this gets addressed and resolved.
            size({
                apply({ availableWidth, elements }) {
                    Object.assign(elements.floating.style, {
                        maxWidth: availableWidth < theme.sizes[8] ? `${availableWidth}px` : `${theme.sizes[8]}px`,
                    });
                },
            }),
        ],
        onOpenChange: setVisible,
        open: visible,
        placement,
        // Only use autoUpdate option if your floating element is conditionally rendered, not hidden with CSS.
        // Use an Effect to call and clean up autoUpdate in the latter scenario.
        whileElementsMounted: autoUpdate,
    });
    // We check which events should be used to show the tooltip via enabled and trigger
    const hover = useHover(context, { enabled: trigger === "hover" });
    const click = useClick(context, { enabled: trigger === "click" });
    const focus = useFocus(context, { enabled: trigger === "focus" });
    // To automatically apply ARIA props to the reference and floating elements to ensure
    // they’re accessible to assistive technology, including item elements if narrowly specified.
    const role = useRole(context, { role: "tooltip" });
    const { getFloatingProps, getReferenceProps } = useInteractions([role, click, focus, hover]);
    useEffect(() => {
        let timer;
        if (autoHide) {
            timer = setTimeout(() => setVisible(false), TOOLTIP_HIDE_DELAY);
        }
        return () => clearTimeout(timer);
    }, [autoHide, visible]);
    return (React.createElement(React.Fragment, null,
        React.cloneElement(React.Children.only(children), Object.assign({ ref: refs.setReference }, getReferenceProps())),
        visible && (React.createElement(TooltipBubble, { arrowRef: arrowRef, context: context, floatingProps: getFloatingProps(), floatingStyles: floatingStyles, targetElemRef: refs.setFloating, testId: testId, variant: variant }, content))));
};
